import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { startLogin } from '@/auth/oauth'
import { loadTokens } from '@/auth/tokens'
import { CLIENT_ID } from '@/auth/config'

export default function Login() {
  const [error, setError] = useState<string | null>(null)
  const [tokens] = useState(() => loadTokens())

  useEffect(() => {
    document.title = 'Lensify — Sign in'
  }, [])

  if (tokens) return <Navigate to="/" replace />

  const onClick = async () => {
    try {
      await startLogin()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <div className="min-h-screen flex flex-col px-6">
      <div className="flex-1 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="flex items-center gap-4 mb-3">
            <img src="/logo-256.png" alt="Lensify" className="w-20 h-20 rounded-2xl" />
            <div>
              <div className="text-3xl font-semibold leading-tight">Lensify</div>
              <div className="text-sm text-stone-500 mt-1">
                by{' '}
                <a
                  href="https://bijansabet.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-fg hover:underline"
                >
                  @bijan
                </a>
              </div>
            </div>
          </div>
          <h1 className="mt-8 text-4xl font-semibold mb-3 leading-tight">
            A new way to see your fitness history.
          </h1>
          <p className="text-stone-600 mb-8 leading-relaxed">
            Years of recovery, strain, sleep, and workouts from your Whoop. In one
            beautiful view.
          </p>
          <ul className="space-y-1.5 mb-8 text-sm text-stone-700">
            {[
              'Calendar heatmaps across years',
              'Day-of-week and seasonal patterns',
              'Insights on your fitness data',
              'Instant — everything lives on your device',
              'Export to CSV, HTML, or Markdown',
            ].map((feat) => (
              <li key={feat} className="flex items-start gap-2.5">
                <span className="text-brand mt-0.5">→</span>
                <span>{feat}</span>
              </li>
            ))}
          </ul>
          {!CLIENT_ID && (
            <div className="mb-6 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
              <code>VITE_WHOOP_CLIENT_ID</code> is not set. Copy <code>.env.example</code> to{' '}
              <code>.env</code> and add your Whoop OAuth app's client ID.
            </div>
          )}
          <button
            onClick={onClick}
            disabled={!CLIENT_ID}
            className="w-full py-3 rounded-lg bg-brand text-white font-medium hover:bg-brand-fg disabled:bg-stone-200 disabled:text-stone-400 transition-colors shadow-sm"
          >
            Connect Whoop
          </button>
          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
          <p className="mt-6 text-xs text-stone-500">You'll sign in at whoop.com.</p>
        </div>
      </div>
      <footer className="text-center text-xs text-stone-500 py-6">
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-stone-600 hover:text-brand-fg transition-colors"
        >
          Privacy
        </a>{' '}
        · Designed in Nantucket, Massachusetts · © 2026
      </footer>
    </div>
  )
}
